import {
  ChangeDetectorRef,
  ComponentRef,
  Directive,
  ElementRef,
  Inject,
  Input,
  ViewContainerRef,
} from '@angular/core';
import {
  combineLatest,
  EMPTY,
  filter,
  from,
  fromEvent,
  Observable,
  switchMap,
  tap,
} from 'rxjs';
import { ComponentStore } from '@ngrx/component-store';
import { DOCUMENT } from '@angular/common';
import { isScullyGenerated, isScullyRunning } from '@scullyio/ng-lib';

export interface LoadingComponent {
  el: ElementRef<HTMLElement>;
  message?: string;
}

const light = 'light';
const flat = 'flat';
const wheel = 'wheel';
type LoadingType = typeof light | typeof flat | typeof wheel;

interface ComponentsConfig {
  [id: string]: {
    component: () => Promise<any>;
  };
}

const componentsConfig: ComponentsConfig = {
  [light]: {
    component: () =>
      import('@nx-icilalune/icilalune/loading/loading-light').then(
        (it) => it.LoadingLightComponent
      ),
  },
  [flat]: {
    component: () =>
      import('@nx-icilalune/icilalune/loading/loading-flat').then(
        (it) => it.LoadingFlatComponent
      ),
  },
  [wheel]: {
    component: () =>
      import('@nx-icilalune/icilalune/loading/loading-wheel').then(
        (it) => it.LoadingWheelComponent
      ),
  },
};

interface LoadingDirectiveStore {
  loading: boolean;
  loadingType: LoadingType;
  element?: HTMLElement;
  message?: string;
  componentRef?: ComponentRef<LoadingComponent>;
}

@Directive({
  selector: '[i2lLoading]',
  standalone: true,
})
export class LoadingDirective extends ComponentStore<LoadingDirectiveStore> {
  readonly loading$ = this.select((s) => s.loading);
  readonly loadingType$ = this.select((s) => s.loadingType);
  readonly message$ = this.select((s) => s.message);

  createComponent = this.effect(
    (combined$: Observable<[boolean, LoadingType]>) => {
      return combined$.pipe(
        filter(([loading, loadingType]) => loading && !!loadingType),
        switchMap(([loading, loadingType]) => {
          if (loading) {
            const componentConfig = componentsConfig[loadingType];
            if (componentConfig) {
              return from(componentConfig.component()).pipe(
                tap((componentInstance) => {
                  const lastLoadingValue = this.get((s) => s.loading);
                  if (lastLoadingValue) {
                    const componentRef =
                      this.viewContainer.createComponent<LoadingComponent>(
                        componentInstance
                      );
                    componentRef.setInput(
                      'message',
                      this.get((s) => s.message)
                    );
                    componentRef.changeDetectorRef.detectChanges();
                    this.patchState({
                      element: componentRef.instance.el.nativeElement,
                      componentRef,
                    });
                    this.cdr.detectChanges();
                  }
                })
              );
            }
          }
          this.clear();
          return EMPTY;
        })
      );
    }
  );

  setMessage = this.effect((message$: Observable<string | undefined>) => {
    return message$.pipe(
      tap((message) => {
        const componentRef = this.get((s) => s.componentRef);
        if (message && componentRef) {
          componentRef.setInput('message', message);
          this.cdr.detectChanges();
        }
      })
    );
  });

  clearAfterDestroyAnimation = this.effect(
    (destroyAnimationEvent$: Observable<any>) => {
      return destroyAnimationEvent$.pipe(
        tap((destroyAnimationEvent: any) => {
          if (destroyAnimationEvent.animationName === 'loadingDestroy') {
            this.clear(true);
          }
        })
      );
    }
  );

  setLoading = this.effect((loading$: Observable<boolean>) => {
    return loading$.pipe(
      tap((loading) => {
        //if (!loading) {
        const loadingElement: HTMLElement | undefined = this.get(
          (s) => s.element
        );
        if (loading === false) {
          if (loadingElement) {
            loadingElement.classList.add('destroy');
            this.clearAfterDestroyAnimation(
              fromEvent(loadingElement, 'animationend')
            );
          } else {
            this.clear(true);
          }
        }
      })
    );
  });

  constructor(
    @Inject(DOCUMENT)
    private document: Document,
    private viewContainer: ViewContainerRef,
    private cdr: ChangeDetectorRef
  ) {
    super({ loading: false, loadingType: 'light' });
    this.createComponent(combineLatest([this.loading$, this.loadingType$]));
    this.setMessage(this.message$);
    this.setLoading(this.loading$);
    if (!isScullyGenerated() && !isScullyRunning()) {
      this.document.body.classList.add('loaded');
    }
  }

  @Input() set i2lLoading(loading: boolean | undefined) {
    if (typeof loading === 'boolean') {
      this.patchState({ loading });
    }
  }

  @Input() set i2lLoadingLoadingType(loadingType: LoadingType) {
    this.patchState({ loadingType });
  }

  @Input() set i2lLoadingMessage(message: string) {
    this.patchState({ message });
  }

  clear(noMoreLoading: boolean = false) {
    this.viewContainer.clear();

    setTimeout(() => {
      try {
        this.cdr.detectChanges();
      } catch (e) {}
    });

    this.patchState({
      element: undefined,
      componentRef: undefined,
    });

    if (noMoreLoading && !isScullyRunning() && isScullyGenerated()) {
      setTimeout(() => {
        this.document.body.classList.add('loaded');
        const scullyRoot = this.document.getElementsByTagName(
          'i2l-app-root-scully'
        );
        const scullyRootElement = scullyRoot.item(0);
        if (scullyRootElement) {
          scullyRootElement.remove();
        }
      }, 750);
      setTimeout(() => {
        this.document.body.classList.add('scully-removed');
      }, 900);
    }
  }
}
